// Public API for zone land-data extraction.
//
// fetchZoneLandData(zone) fetches every applicable layer in parallel and
// never rejects because of a single layer failure (fail-open per layer).
// getZoneLandData(zone) adds an in-memory cache keyed on zone id + polygon,
// so an edited boundary triggers a refetch. No persistence.

import { ZONE_LAND_LAYERS } from "./layers";
import { fetchArcgisFeatures } from "./arcgisExtractor";
import { fetchWfsFeatures, polygonBbox } from "./wfsExtractor";
import { computeGradedBreakdown, computeCoveredFraction } from "./intersect";
import { bboxClipFeatures, growBbox, bboxPolygon } from "./bboxClip";
import { reportWarn } from "../report";

function bboxesIntersect([w1, s1, e1, n1], [w2, s2, e2, n2]) {
  return w1 <= e2 && w2 <= e1 && s1 <= n2 && s2 <= n1;
}

/**
 * How far beyond the field the soil legend looks, in metres (BBB-427).
 *
 * A field carries a median of 2 soil classes, so a legend built strictly from
 * inside the boundary is often a single swatch, and a farmer reading it cannot
 * tell "one soil type here" from "the map has nothing for me".
 *
 * CONFIRMED by Davin 11/09/2026: 250 m stays, as a fixed distance. It has no
 * agronomic basis and that is known: it came from measuring six 20 ha England
 * fields on 21/08/2026, where it added classes without adding noise and 1000 m
 * bought nothing. Scaling it with field size was considered and rejected.
 *
 * Soil only: the soil legend is the only consumer of a nearby tier. Every other
 * layer, and the Land characteristics panel, stays strictly inside the boundary.
 */
export const NEARBY_SCAN_METRES = 250;

// Whether every ring has enough positions to be a ring at all.
//
// `coordinates: [[]]` cleared the old length guard, because the OUTER array has
// one entry, and then closedPolygon read `ring[0]` on an empty ring and threw. It
// surfaced as a rejected fetch, so a farmer was told the layer was not available
// when the real problem was a zone we could not read. Three positions is the
// crash threshold, not a geometry judgement: a shorter ring cannot be closed, and
// a degenerate-but-closable ring just yields zero area, which callers handle.
function isPosition(position) {
  return (
    Array.isArray(position) &&
    position.length >= 2 &&
    Number.isFinite(position[0]) &&
    Number.isFinite(position[1])
  );
}

function hasUsableRings(coordinates) {
  // Every position, not just the ring count. Checking the count alone still let
  // `[null, [1, 2], [3, 4]]` through, and closedPolygon then dereferenced a null.
  // Validating the positions closes the class rather than the instance: a null, a
  // string coordinate, a NaN and a one-number position all fail here now.
  return coordinates.every(
    (ring) => Array.isArray(ring) && ring.length >= 3 && ring.every(isPosition)
  );
}

// Turf requires closed linear rings; zones drawn in the app may store an
// unclosed ring (Google Maps paths don't repeat the first vertex).
function closedPolygon(polygon) {
  const coordinates = polygon.coordinates.map((ring) => {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return ring;
    return [...ring, first];
  });
  return { type: "Polygon", coordinates };
}

// Cheap stable hash of the polygon coordinates, so cache entries invalidate
// when a zone boundary is edited.
function hashCoordinates(coordinates) {
  const str = JSON.stringify(coordinates);
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0; // djb2
  }
  return `${str.length}:${hash}`;
}

/**
 * The id this module keys its cache on. Exported so a consumer can ask "is the
 * data I am holding this zone's data?" without restating the field precedence
 * and drifting from it.
 */
export function getZoneId(zone) {
  return zone?._id || zone?.zone_id || zone?.id || null;
}

/**
 * A key that answers "is this the same field as the one I am already showing?"
 *
 * The id alone cannot: `getZoneId` returns null for a zone carrying none, so two
 * different ID-less fields compare equal and a consumer holding field A's data
 * would go on showing it while field B loads, labelled as field B's. Falling
 * back to the boundary hash gives those fields a stable identity of their own.
 *
 * The id still wins when there is one, deliberately: a farmer editing a field's
 * boundary keeps the same id, and that is a re-fetch of the same field rather
 * than a different field opening.
 */
export function getZoneIdentity(zone) {
  const coordinates = zone?.polygon_shape?.coordinates;
  if (!coordinates?.length) return getZoneId(zone) || null;
  // Both halves are load-bearing, and each has been a bug on its own. The id
  // alone is wrong because a farmer editing a boundary keeps the same id, so the
  // answer for the old shape would stand for the new one. The boundary alone is
  // wrong because a zone may carry no id at all, and two ID-less fields with the
  // same shape really are the same request.
  return `${getZoneId(zone)}|${hashCoordinates(coordinates)}`;
}

async function fetchLayerResult(layer, zonePolygon, zoneBbox) {
  const base = {
    layerId: layer.id,
    title: layer.title,
    category: layer.category,
    kind: layer.kind,
  };
  if (layer.scaleDenominator) base.scaleDenominator = layer.scaleDenominator;

  // Soil layers are scanned a little beyond the boundary so the legend can name
  // what is around the field as well as in it (BBB-427). ONE fetch, at the grown
  // box: the answers for the field come from clipping that same feature set back
  // to the field, so the panel is unaffected and there is no second request.
  // `legendClassField` too: a soil layer without one feeds no legend (Scotland's
  // 1:25k has none), so widening its query would buy nothing and cost bytes.
  const scansWide = layer.category === "soil" && Boolean(layer.legendClassField);
  const scanBbox = scansWide ? growBbox(zoneBbox, NEARBY_SCAN_METRES) : zoneBbox;
  // Both fetchers take a geometry, so the grown box travels as a rectangle: WFS
  // reduces it to a BBOX parameter, ArcGIS sends it as query rings.
  const queryPolygon = scansWide ? bboxPolygon(scanBbox) : zonePolygon;

  try {
    const { features: fetched, truncated } =
      layer.protocol === "wfs"
        ? await fetchWfsFeatures(layer, queryPolygon)
        : await fetchArcgisFeatures(layer, queryPolygon);

    // Both servers filter on a feature's BOUNDING BOX, not its geometry, so a
    // small field can pull in whole coastal multipolygons that never touch it.
    // Clipping to the query bbox first is answer-preserving (the query geometry
    // is a subset of its own bbox) and is the difference between ~10 s and
    // ~100 ms of blocking compute on zone-open. See bboxClip.js.
    const features = bboxClipFeatures(fetched, scanBbox);

    if (truncated) {
      reportWarn(
        `zoneLandData: layer ${layer.id} truncated: server had more features than it returned; coverage may under-report`
      );
    }

    if (layer.kind === "graded") {
      // Clipped to the FIELD. Every fraction here is a fraction of the field's
      // area, which is what the Land characteristics panel presents. Widening
      // this would silently turn its percentages into fractions of a rectangle.
      const breakdown = computeGradedBreakdown(
        zonePolygon,
        features,
        layer.classCodeField,
        layer.classNameField,
        layer.classLinkField,
        layer.legendClassField
      );

      // Second breakdown over the SAME features, clipped to the grown rectangle.
      // Measured at 24 ms. Kept on its own key so nothing that reads the panel's
      // numbers can pick it up by accident: fractions here are of the rectangle,
      // not of the field.
      if (scansWide) {
        const nearby = computeGradedBreakdown(
          queryPolygon,
          features,
          layer.classCodeField,
          layer.classNameField,
          layer.classLinkField,
          layer.legendClassField
        );
        return {
          ...base,
          status: "ok",
          truncated,
          ...breakdown,
          nearbyLegendClasses: nearby.legendClasses,
          nearbyScanMetres: NEARBY_SCAN_METRES,
        };
      }

      return { ...base, status: "ok", truncated, ...breakdown };
    }

    return {
      ...base,
      status: "ok",
      truncated,
      coveredFraction: computeCoveredFraction(zonePolygon, features),
    };
  } catch (err) {
    reportWarn(`zoneLandData: layer ${layer.id} failed`, err);
    return { ...base, status: "error" };
  }
}

/**
 * Fetch land data for a zone across all applicable layers.
 * @param {object} zone zone document (needs `polygon_shape` GeoJSON Polygon)
 * @returns {Promise<{zoneId, fetchedAt, status?, layers}>}
 */
export async function fetchZoneLandData(zone) {
  const zoneId = getZoneId(zone);

  const coordinates = zone?.polygon_shape?.coordinates;
  // Marker-only zones exist, and so does a stored shape we cannot read. Both are
  // "there is no polygon here to answer about", which is what this status says,
  // and it is a truer answer than a fetch failure.
  if (!coordinates?.length || !hasUsableRings(coordinates)) {
    return { zoneId, fetchedAt: new Date().toISOString(), status: "no-polygon", layers: {} };
  }

  const zonePolygon = closedPolygon(zone.polygon_shape);
  const zoneBbox = polygonBbox(zonePolygon);

  const results = await Promise.allSettled(
    ZONE_LAND_LAYERS.map((layer) => {
      if (!bboxesIntersect(zoneBbox, layer.coverageBounds)) {
        return Promise.resolve({
          layerId: layer.id,
          title: layer.title,
          category: layer.category,
          kind: layer.kind,
          status: "out-of-coverage",
        });
      }
      return fetchLayerResult(layer, zonePolygon, zoneBbox);
    })
  );

  const layers = {};
  results.forEach((result, i) => {
    const layer = ZONE_LAND_LAYERS[i];
    layers[layer.id] =
      result.status === "fulfilled"
        ? result.value
        : {
            layerId: layer.id,
            title: layer.title,
            category: layer.category,
            kind: layer.kind,
            status: "error",
          };
  });

  return { zoneId, fetchedAt: new Date().toISOString(), layers };
}

const cache = new Map();

// One farmer views a handful of zones per session, but each boundary edit adds a
// new key, so cap the map and evict oldest-first (Map preserves insertion order)
// to stop it growing unbounded over a long session.
const CACHE_MAX_ENTRIES = 50;

/**
 * Cached variant of fetchZoneLandData. Keyed on getZoneIdentity, so an edited
 * boundary is a different request and refetches. In-memory only, capped at
 * CACHE_MAX_ENTRIES with oldest-first eviction. useZoneSoilLegend keys
 * "have I already asked for this?" on the same function, deliberately: if the
 * two ever drifted, one of them would be answering about the wrong shape.
 */
export function getZoneLandData(zone) {
  if (!zone?.polygon_shape?.coordinates?.length) return fetchZoneLandData(zone);

  const key = getZoneIdentity(zone);
  if (!cache.has(key)) {
    const promise = fetchZoneLandData(zone).catch((err) => {
      cache.delete(key); // don't cache a rejection
      throw err;
    });
    cache.set(key, promise);
    while (cache.size > CACHE_MAX_ENTRIES) {
      cache.delete(cache.keys().next().value); // evict oldest
    }
  }
  return cache.get(key);
}

/** Test hook — clears the in-memory cache. */
export function clearZoneLandDataCache() {
  cache.clear();
}
